import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ModuleContext = createContext();

export const MODULES = {
  FUTURES: 'futures',
  CFD: 'cfd',
  ARBICSX_CFD: 'arbicsx_cfd'
};

export const MODULE_LABELS = {
  [MODULES.FUTURES]: 'AntiFutures',
  [MODULES.CFD]: 'Arbitex',
  [MODULES.ARBICSX_CFD]: 'Arbicsx'
};

// Module accent colors
export const MODULE_COLORS = {
  [MODULES.FUTURES]: {
    primary: '#26A17B',      // USDT #26A17B
    primaryHsl: '161 62% 39%',  // HSL for Tailwind --primary
    primaryHover: '#1f8a68',
    primaryLight: 'rgba(38,161,123, 0.2)',
    primaryBorder: 'rgba(38,161,123, 0.3)',
    text: '#26A17B'
  },
  [MODULES.CFD]: {
    primary: '#38bdf8',      // blu azzurrino
    primaryHsl: '198 93% 60%',  // HSL for Tailwind --primary
    primaryHover: '#0ea5e9',
    primaryLight: 'rgba(56, 189, 248, 0.2)',
    primaryBorder: 'rgba(56, 189, 248, 0.3)',
    text: '#7dd3fc'
  },
  [MODULES.ARBICSX_CFD]: {
    primary: '#26A17B',      // USDT #26A17B
    primaryHsl: '161 62% 39%',  // HSL for Tailwind --primary
    primaryHover: '#1f8a68',
    primaryLight: 'rgba(38,161,123, 0.2)',
    primaryBorder: 'rgba(38,161,123, 0.3)',
    text: '#26A17B'
  }
};

const ADMIN_EMAIL = 'eliabonacci92@gmail.com';

export function ModuleProvider({ children }) {
  const { user } = useAuth();
  
  // UI Arbitex/AntiFutures rimossa: l'unico modulo visibile è Arbicsx CFD
  const [activeModule, setActiveModule] = useState(MODULES.ARBICSX_CFD);
  
  // Impersonation state (admin only)
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  // Check if current user is admin (primary or secondary)
  const isAdminPrimary = user?.email === ADMIN_EMAIL || user?.role === 'admin' || user?.role === 'admin_primary';
  const isAdminSecondary = user?.role === 'admin_secondary';
  const isAdmin = isAdminPrimary || isAdminSecondary;
  
  // Check if user is approved (only relevant for arbicsx_cfd)
  const isApproved = user?.is_approved || isAdmin;
  
  // Check module access
  // Only admin_primary can access Arbitex Futures and CFD (admin_secondary sees only Arbicsx)
  const canAccessArbitex = isAdminPrimary && !isAdminSecondary;
  const canAccessArbicsx = isApproved; // Approved users and admin can access Arbicsx CFD

  // Force correct module based on user permissions
  // Non-admin users and admin_secondary can ONLY access Arbicsx CFD
  useEffect(() => {
    if (user && (!isAdminPrimary || isAdminSecondary)) {
      // Non-admin primary users must always be on Arbicsx CFD
      if (activeModule !== MODULES.ARBICSX_CFD) {
        setActiveModule(MODULES.ARBICSX_CFD);
      }
    }
  }, [user, isAdminPrimary, isAdminSecondary, activeModule]);

  // When impersonating, always switch to Arbicsx CFD
  useEffect(() => {
    if (impersonatedUser && activeModule !== MODULES.ARBICSX_CFD) {
      setActiveModule(MODULES.ARBICSX_CFD);
    }
  }, [impersonatedUser, activeModule]);

  useEffect(() => {
    localStorage.setItem('hedging-tracker-module', activeModule);
  }, [activeModule]);

  // Start impersonation
  const startImpersonation = (targetUser) => {
    if (isAdmin) {
      setImpersonatedUser(targetUser);
      localStorage.setItem('impersonated-user', JSON.stringify(targetUser));
    }
  };

  // Update impersonated user data (e.g., after photo upload)
  const updateImpersonatedUser = (updatedData) => {
    if (isAdmin && impersonatedUser) {
      const updated = { ...impersonatedUser, ...updatedData };
      setImpersonatedUser(updated);
      localStorage.setItem('impersonated-user', JSON.stringify(updated));
    }
  };

  // Stop impersonation
  const stopImpersonation = () => {
    setImpersonatedUser(null);
    localStorage.removeItem('impersonated-user');
  };

  // Restore impersonation from localStorage
  useEffect(() => {
    if (isAdminPrimary) {
      const saved = localStorage.getItem('impersonated-user');
      if (saved) {
        try {
          setImpersonatedUser(JSON.parse(saved));
        } catch (e) {
          localStorage.removeItem('impersonated-user');
        }
      }
    } else {
      // Clear impersonation if not admin primary
      setImpersonatedUser(null);
      localStorage.removeItem('impersonated-user');
    }
  }, [isAdminPrimary]);

  const value = {
    activeModule,
    setActiveModule,
    isFutures: activeModule === MODULES.FUTURES,
    isCFD: activeModule === MODULES.CFD,
    isArbicsxCFD: activeModule === MODULES.ARBICSX_CFD,
    moduleLabel: MODULE_LABELS[activeModule],
    moduleColors: MODULE_COLORS[activeModule],
    
    // User permissions
    isAdmin,
    isAdminPrimary,
    isAdminSecondary,
    isApproved,
    canAccessArbitex,
    canAccessArbicsx,
    
    // Impersonation (only for admin_primary)
    impersonatedUser,
    startImpersonation,
    updateImpersonatedUser,
    stopImpersonation,
    isImpersonating: !!impersonatedUser,
    
    // Helper for API calls
    getImpersonateParam: () => impersonatedUser ? `?impersonate_user_id=${impersonatedUser.id}` : ''
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModule() {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
}

export default ModuleContext;
